import axios, { AxiosResponse } from 'axios';
import i18next, { BackendModule, i18n, InitOptions, Module, ResourceKey } from 'i18next';
import LanguageDetector, { CustomDetector } from 'i18next-browser-languagedetector';
import { debounce, isEqual } from 'lodash';
import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import { DomainCustomConfig } from '../auth';
import { captureException, captureMessage, MiddleSpinner } from '../components';
import { useSwitchAwareConfig } from '../hooks';

interface DynamicBackendOptions {
  baseUrl: string;
  config: Partial<DomainCustomConfig>;
}

type ReadCallback = (err: Error | null, data: ResourceKey | null) => void;

class DynamicBackend implements BackendModule<DynamicBackendOptions> {
  public static readonly type: 'backend' = 'backend';
  public readonly type: 'backend' = 'backend';

  constructor(private readonly options: DynamicBackendOptions) { }

  public init(): void { }

  public async read(language: string, namespace: string, callback: ReadCallback): Promise<void> {
    try {
      let url: string;
      if (namespace === 'terms' && this.options.config.localazy && this.options.config.localazy.terms) {
        url = `${this.options.baseUrl}/locales/${this.options.config.localazy.terms}/${language}/terms.json`;
      } else {
        url = `${this.options.baseUrl}/locales/${language}/${namespace}.json`;
      }

      const response: AxiosResponse<ResourceKey> = await axios.get(url, {
        params: {
          v: import.meta.env.VITE_BUILD ?? '',
        },
      });

      if (response.status === 200) {
        callback(null, response.data);
      } else {
        callback(new Error(`Failed to load ${namespace} for ${language}`), null);
      }
    } catch (error) {
      console.error(`Error loading ${namespace} for ${language}:`, error);
      callback(error instanceof Error ? error : new Error('Unknown error occurred'), null);
    }
  }
}

const isProduction = import.meta.env.PROD;

const processZhLanguage = (lang: string): string => {
  if (lang.startsWith('zh') && !lang.includes('Hant')) {
    return `zh-Hant${lang.substring(2)}`;
  }
  return lang;
};

const customDetector: CustomDetector = {
  name: 'customDetector',
  lookup: (_options) => {
    const detectedLang = (window.navigator.languages && window.navigator.languages.length > 0)
      ? window.navigator.languages[0]
      : window.navigator.language;
    return processZhLanguage(detectedLang);
  },
};

const localStorageDetector: CustomDetector = {
  name: 'localStorageDetector',
  lookup: (_options) => {
    const lang = localStorage.getItem('portal-language-preference');
    if (lang && lang !== 'null') {
      const cleanLang = lang.replace(/^"|"$/g, '').replace('_', '-'); // Remove quotes and replace underscore with hyphen
      return processZhLanguage(cleanLang);
    }
    return undefined;
  },
};

const reportMissingKey = debounce((namespace: string, key: string, fallbackValue: string | undefined): void => {
  captureMessage(`Missing translation key: ${namespace}:${key}`, {
    level: 'warning',
    tags: {
      namespace,
      fallbackValue,
    },
  });
}, 2000);

const enabledNamespaces = ['common', 'auth', 'admin', 'track', 'manage', 'terms', 'l10n', 'data-subscription'];

const updateDocumentLanguage = (language?: string): void => {
  if (!language || typeof document === 'undefined') return;

  const normalizedLanguage = language.replace('_', '-');
  if (document.documentElement.lang !== normalizedLanguage) {
    document.documentElement.lang = normalizedLanguage;
  }
};

const SUPPORTED_LOCALES = [
  'en', // English
  'id', // Indonesian
  'ja', // Japanese
  'ko', // Korean
  'ms', // Malay
  'es-MX', // Mexican Spanish
  'es', // Spanish
  'th', // Thai
  'zh-Hant', // Traditional Chinese
  'vi', // Vietnamese
];

export class I18n {
  private static instance: i18n;

  public static getInstance(): i18n {
    if (!this.instance) {
      this.instance = i18next.createInstance();
    }
    return this.instance;
  }

  public static async initialize(baseUrl: string, config: Partial<DomainCustomConfig>): Promise<void> {
    const languageDetector = new LanguageDetector();
    languageDetector.addDetector(customDetector);
    languageDetector.addDetector(localStorageDetector);

    this.instance = i18next.createInstance();

    try {
      await this.instance
        .use(languageDetector)
        .use(new DynamicBackend({
          baseUrl,
          config,
        }) as Module)
        .use(initReactI18next)
        .init({
          fallbackLng: 'en',
          supportedLngs: SUPPORTED_LOCALES,
          debug: false,
          interpolation: {
            escapeValue: false,
          },
          ns: enabledNamespaces,
          defaultNS: 'common',
          detection: {
            order: ['localStorageDetector', 'customDetector', 'querystring', 'cookie', 'htmlTag'],
            caches: ['localStorage'],
          },
          react: {
            useSuspense: true,
          },
          missingKeyHandler: (lngs, namespace, key, fallbackValue) => {
            if (isProduction && lngs.includes('en')) {
              reportMissingKey(namespace, key, fallbackValue);
            }
          },
          saveMissing: isProduction,
          preload: ['en'],
        } as InitOptions);
    } catch (error) {
      console.error('Error initializing i18n:', error);
      captureException(error);
      throw error;
    }
  }
}

export const DomainI18nProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isI18nInitialized, setIsI18nInitialized] = useState(false);
  const config = useSwitchAwareConfig();
  const [stableConfig, setStableConfig] = useState(config);

  useEffect(() => {
    if (!isEqual(config, stableConfig)) {
      setStableConfig(config);
    }
  }, [config, stableConfig]);

  useEffect(() => {
    const init = async (): Promise<void> => {
      const baseUrl = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
      setIsI18nInitialized(false);
      await I18n.initialize(baseUrl, stableConfig);
      setIsI18nInitialized(true);
    };

    void init();
  }, [stableConfig]);

  useEffect(() => {
    if (!isI18nInitialized) return;

    const i18nInstance = I18n.getInstance();
    const handleLanguageChanged = (language: string): void => {
      updateDocumentLanguage(language);
    };

    updateDocumentLanguage(i18nInstance.resolvedLanguage ?? i18nInstance.language);
    i18nInstance.on('languageChanged', handleLanguageChanged);

    return () => {
      i18nInstance.off('languageChanged', handleLanguageChanged);
    };
  }, [isI18nInitialized]);

  if (!isI18nInitialized) return <MiddleSpinner />;

  return (
    <I18nextProvider i18n={I18n.getInstance()}>
      {children}
    </I18nextProvider>
  );
};

